import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Search, 
  Edit, 
  Eye, 
  Calendar,
  Clock,
  User,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  ExternalLink
} from 'lucide-react'
import { consultationService, Consultation } from '../services/consultationService'

const Consultations = () => {
  const [consultations, setConsultations] = useState<Consultation[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [limit] = useState(10)
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [updateStatus, setUpdateStatus] = useState<'PENDING' | 'SCHEDULED' | 'COMPLETED' | 'CANCELLED'>('PENDING')
  const [updateNotes, setUpdateNotes] = useState('')

  const fetchConsultations = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params: any = {
        page: currentPage,
        limit: limit,
      }
      if (statusFilter !== 'ALL') {
        params.status = statusFilter
      }
      if (searchTerm) {
        params.search = searchTerm
      }
      
      const response = await consultationService.getConsultations(params)
      if (response.success && response.data) {
        setConsultations(response.data)
        if (response.pagination) {
          setTotalPages(response.pagination.pages)
          setTotalCount(response.pagination.total)
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to fetch consultations')
      console.error('Error fetching consultations:', error)
    } finally {
      setLoading(false)
    }
  }, [currentPage, statusFilter, searchTerm, limit])

  useEffect(() => {
    fetchConsultations()
  }, [fetchConsultations])

  const handleView = (consultation: Consultation) => {
    setSelectedConsultation(consultation)
    setShowModal(true)
  }

  const handleEdit = (consultation: Consultation) => {
    setSelectedConsultation(consultation)
    setUpdateStatus(consultation.status)
    setUpdateNotes(consultation.notes || '')
    setShowUpdateModal(true)
  }

  const handleUpdate = async () => {
    if (!selectedConsultation) return
    
    try {
      setActionLoading(selectedConsultation._id)
      await consultationService.updateConsultation(selectedConsultation._id, {
        status: updateStatus,
        notes: updateNotes,
      })
      await fetchConsultations()
      setShowUpdateModal(false)
      setSelectedConsultation(null)
    } catch (error: any) {
      setError(error.message || 'Failed to update consultation')
    } finally {
      setActionLoading(null)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending', icon: Clock },
      SCHEDULED: { color: 'bg-blue-100 text-blue-800', text: 'Scheduled', icon: Calendar },
      COMPLETED: { color: 'bg-green-100 text-green-800', text: 'Completed', icon: CheckCircle },
      CANCELLED: { color: 'bg-red-100 text-red-800', text: 'Cancelled', icon: XCircle }
    }
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING
    const Icon = config.icon
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color} flex items-center space-x-1 w-fit`}>
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </span>
    )
  }

  const getConsultationTypeName = (consultation: Consultation) => {
    if (typeof consultation.consultationTypeId === 'object' && consultation.consultationTypeId) {
      return consultation.consultationTypeId.name
    }
    return 'Unknown Type'
  }

  const stats = {
    total: totalCount,
    pending: consultations.filter(c => c.status === 'PENDING').length,
    scheduled: consultations.filter(c => c.status === 'SCHEDULED').length,
    completed: consultations.filter(c => c.status === 'COMPLETED').length,
    cancelled: consultations.filter(c => c.status === 'CANCELLED').length,
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Consultations</h1>
          <p className="text-gray-600">Manage customer consultation requests</p>
        </div>
        <div className="text-sm text-gray-500">
          {consultations.length} of {totalCount} consultations
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Consultations', value: stats.total, color: 'bg-blue-500', icon: Calendar },
          { label: 'Pending', value: stats.pending, color: 'bg-yellow-500', icon: Clock },
          { label: 'Scheduled', value: stats.scheduled, color: 'bg-indigo-500', icon: Calendar },
          { label: 'Completed', value: stats.completed, color: 'bg-green-500', icon: CheckCircle }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
              <div className="flex items-center justify-between">
                <div>
                <p className="text-sm font-medium text-gray-600 mb-2">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
              <div className={`p-4 rounded-2xl ${stat.color} shadow-lg`}>
                <stat.icon className="h-8 w-8 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
              placeholder="Search by name, email, or phone..."
                value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
              />
            </div>
            <select
              value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
              className="w-full sm:w-48 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          <button
            onClick={fetchConsultations}
            disabled={loading}
            className="px-4 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Consultations Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Client</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Type</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Scheduled</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Created</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-5 h-5 animate-spin text-amber-500" />
                      <span className="text-gray-500">Loading consultations...</span>
                    </div>
                  </td>
                </tr>
              ) : consultations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No consultations found
                  </td>
                </tr>
              ) : (
                consultations.map((consultation, index) => (
                <motion.tr
                  key={consultation._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                    <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{consultation.name}</p>
                        <p className="text-sm text-gray-500">{consultation.email}</p>
                        {consultation.phone && (
                          <p className="text-sm text-gray-600">{consultation.phone}</p>
                        )}
                      </div>
                    </div>
                  </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-gray-900">
                        {getConsultationTypeName(consultation)}
                      </span>
                  </td>
                    <td className="py-4 px-6">
                    {getStatusBadge(consultation.status)}
                  </td>
                    <td className="py-4 px-6">
                      <div className="text-sm text-gray-600">
                        {consultation.scheduledDateTime ? (
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(consultation.scheduledDateTime).toLocaleString()}
                          </div>
                        ) : (
                          <span className="text-gray-400">Not scheduled</span>
                        )}
                      </div>
                  </td>
                    <td className="py-4 px-6">
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(consultation.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                    <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <button
                          onClick={() => handleView(consultation)}
                          className="p-2 text-gray-600 hover:text-amber-600 transition-colors rounded-lg hover:bg-amber-50"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                          onClick={() => handleEdit(consultation)}
                          disabled={actionLoading === consultation._id}
                          className="p-2 text-gray-600 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50 disabled:opacity-50"
                        title="Edit Consultation"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalCount)} of {totalCount} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-2 text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* View Consultation Modal */}
      <AnimatePresence>
      {showModal && selectedConsultation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99998] p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-8 shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Consultation Details</h2>
                  <p className="text-gray-600">{selectedConsultation.name}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
              {/* Client Information */}
              <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                      <label className="text-sm font-medium text-gray-600">Name</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.name}</p>
                  </div>
                  <div>
                      <label className="text-sm font-medium text-gray-600">Email</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.email}</p>
                  </div>
                  {selectedConsultation.phone && (
                    <div>
                        <label className="text-sm font-medium text-gray-600">Phone</label>
                        <p className="text-gray-900 mt-1">{selectedConsultation.phone}</p>
                    </div>
                  )}
                  {selectedConsultation.location && (
                    <div>
                        <label className="text-sm font-medium text-gray-600">Location</label>
                        <p className="text-gray-900 mt-1">{selectedConsultation.location}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Consultation Details */}
              <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultation Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Type</label>
                      <p className="text-gray-900 mt-1">{getConsultationTypeName(selectedConsultation)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedConsultation.status)}</div>
                    </div>
                    {selectedConsultation.scheduledDateTime && (
                    <div>
                        <label className="text-sm font-medium text-gray-600">Scheduled Date & Time</label>
                        <p className="text-gray-900 mt-1">
                          {new Date(selectedConsultation.scheduledDateTime).toLocaleString()}
                      </p>
                    </div>
                  )}
                    {selectedConsultation.googleMeetLink && (
                      <div>
                        <label className="text-sm font-medium text-gray-600">Google Meet Link</label>
                        <a
                          href={selectedConsultation.googleMeetLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center space-x-1 mt-1"
                        >
                          <span>Join Meeting</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  {selectedConsultation.completedAt && (
                    <div>
                        <label className="text-sm font-medium text-gray-600">Completed At</label>
                        <p className="text-gray-900 mt-1">
                        {new Date(selectedConsultation.completedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Client Background */}
                {(selectedConsultation.experience || selectedConsultation.goals || selectedConsultation.budget || selectedConsultation.timeframe) && (
              <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Background</h3>
                <div className="space-y-4">
                  {selectedConsultation.experience && (
                    <div>
                          <label className="text-sm font-medium text-gray-600">Experience</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.experience}</p>
                    </div>
                  )}
                  {selectedConsultation.goals && (
                    <div>
                          <label className="text-sm font-medium text-gray-600">Goals</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.goals}</p>
                    </div>
                  )}
                  {selectedConsultation.budget && (
                    <div>
                          <label className="text-sm font-medium text-gray-600">Budget</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.budget}</p>
                    </div>
                  )}
                  {selectedConsultation.timeframe && (
                    <div>
                          <label className="text-sm font-medium text-gray-600">Timeframe</label>
                      <p className="text-gray-900 mt-1">{selectedConsultation.timeframe}</p>
                    </div>
                  )}
                </div>
              </div>
                )}

              {/* Message */}
              {selectedConsultation.message && (
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Message</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                    {selectedConsultation.message}
                  </p>
                </div>
              )}

              {/* Notes */}
                {selectedConsultation.notes && (
              <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin Notes</h3>
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                      {selectedConsultation.notes}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowModal(false)
                    handleEdit(selectedConsultation)
                }}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
              >
                  Edit Consultation
              </button>
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Consultation Modal */}
      <AnimatePresence>
        {showUpdateModal && selectedConsultation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowUpdateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-8 shadow-xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Update Consultation</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={updateStatus}
                    onChange={(e) => setUpdateStatus(e.target.value as any)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
                  >
                    <option value="PENDING">Pending</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes</label>
                  <textarea
                    value={updateNotes}
                    onChange={(e) => setUpdateNotes(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50 h-32"
                    placeholder="Add notes about this consultation..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowUpdateModal(false)
                    setSelectedConsultation(null)
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={actionLoading === selectedConsultation._id}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {actionLoading === selectedConsultation._id ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Consultation'
                  )}
                </button>
        </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}

export default Consultations
